(() => {
  const script = document.querySelector('script[src*="trademark.js"]');
  const trademarkText = (
    script?.dataset.trademarkText
    || window.theme?.settings?.trademarkText
    || ''
  ).trim();
  const trademarkSymbol = (
    script?.dataset.trademarkSymbol
    || window.theme?.settings?.trademarkSymbol
    || ''
  ).trim();

  if (!trademarkText || !trademarkSymbol) return;

  const SKIP_TAGS = new Set([
    'SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA',
    'INPUT', 'SELECT', 'OPTION', 'SUP'
  ]);
  const ALLOWED_TAG_NAMES = [
    'P', 'SPAN', 'A',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
    'LI', 'BUTTON'
  ];
  const ALLOWED_TAGS = new Set(ALLOWED_TAG_NAMES);
  const ALLOWED_SELECTOR = ALLOWED_TAG_NAMES.join(',');
  const textLen = trademarkText.length;
  const symbolLen = trademarkSymbol.length;
  let isApplying = false;

  const isElement = (node) => node?.nodeType === Node.ELEMENT_NODE;
  const isTextNode = (node) => node?.nodeType === Node.TEXT_NODE;
  const canContainText = (node) => (
    isElement(node)
    || node?.nodeType === Node.DOCUMENT_FRAGMENT_NODE
  );

  const isSkippedElement = (el) => (
    isElement(el)
    && (SKIP_TAGS.has(el.tagName) || el.hasAttribute('data-no-trademark'))
  );
  const isAllowedElement = (el) => isElement(el) && ALLOWED_TAGS.has(el.tagName);

  const hasSelfOrAncestor = (el, predicate) => {
    for (let current = el; current; current = current.parentElement) {
      if (predicate(current)) return true;
    }
    return false;
  };

  const isInSkipped = (el) => hasSelfOrAncestor(el, isSkippedElement);
  const isInAllowed = (el) => hasSelfOrAncestor(el, isAllowedElement);

  const isSupSymbol = (el) => (
    el?.nodeType === Node.ELEMENT_NODE
    && el.tagName === 'SUP'
    && el.textContent === trademarkSymbol
  );

  const createSup = () => {
    const sup = document.createElement('sup');
    sup.textContent = trademarkSymbol;
    return sup;
  };

  const processTextNode = (node) => {
    const text = node.nodeValue;
    if (!text) return;

    let pos = text.indexOf(trademarkText);
    if (pos === -1) return;

    const parent = node.parentNode;
    if (!parent) return;

    const fragment = document.createDocumentFragment();
    let cursor = 0;
    let buffer = '';
    let changed = false;

    while (pos !== -1) {
      const afterMatch = pos + textLen;
      buffer += text.slice(cursor, afterMatch);
      cursor = afterMatch;

      const hasInlineSymbol = symbolLen > 0
        && text.slice(afterMatch, afterMatch + symbolLen) === trademarkSymbol;
      const atEnd = afterMatch === text.length;
      const hasSupSibling = atEnd && !hasInlineSymbol && isSupSymbol(node.nextSibling);

      if (!hasSupSibling) {
        fragment.appendChild(document.createTextNode(buffer));
        fragment.appendChild(createSup());
        buffer = '';
        changed = true;
        if (hasInlineSymbol) cursor = afterMatch + symbolLen;
      }

      pos = text.indexOf(trademarkText, cursor);
    }

    if (!changed) return;

    if (cursor < text.length) buffer += text.slice(cursor);
    if (buffer) fragment.appendChild(document.createTextNode(buffer));

    parent.replaceChild(fragment, node);
  };

  const processTextNodeIfNeeded = (node) => {
    const parent = node.parentElement;
    if (!isInSkipped(parent) && isInAllowed(parent)) processTextNode(node);
  };

  const processTextNodesInside = (node) => {
    if (isTextNode(node)) {
      if (node.nodeValue.indexOf(trademarkText) !== -1) processTextNode(node);
      return;
    }

    if (!canContainText(node) || isSkippedElement(node)) return;

    for (let child = node.firstChild; child;) {
      const next = child.nextSibling;
      processTextNodesInside(child);
      child = next;
    }
  };

  const processAllowedElement = (el) => {
    if (!isInSkipped(el)) processTextNodesInside(el);
  };

  const processAllowedDescendants = (root) => {
    root.querySelectorAll(ALLOWED_SELECTOR).forEach((el) => {
      if (!isInAllowed(el.parentElement)) processAllowedElement(el);
    });
  };

  const processRoot = (root) => {
    if (!root || isApplying) return;

    isApplying = true;
    try {
      if (isTextNode(root)) {
        processTextNodeIfNeeded(root);
      } else if (!canContainText(root) || isInSkipped(root)) {
        return;
      } else if (isAllowedElement(root)) {
        processAllowedElement(root);
      } else {
        processAllowedDescendants(root);
      }
    } finally {
      isApplying = false;
    }
  };

  const pending = new Set();
  let scheduled = false;

  const flush = () => {
    scheduled = false;
    if (!pending.size) return;

    try {
      pending.forEach((node) => {
        if (!node.isConnected) return;
        processRoot(node);
      });
    } finally {
      pending.clear();
    }
  };

  const observer = new MutationObserver((mutations) => {
    if (isApplying) return;

    let queued = false;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        pending.add(node);
        queued = true;
      }
    }

    if (queued && !scheduled) {
      scheduled = true;
      requestAnimationFrame(flush);
    }
  });

  const init = () => {
    if (!document.body) return;
    processRoot(document.body);
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  [
    'on:cart-drawer:after-open',
    'on:quickbuy:after-open',
    'on:variant:change',
    'on:facet-filters:updated'
  ].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      const target = event.target instanceof Element ? event.target : document.body;
      processRoot(target);
    });
  });
})();
